import type { ReviewSeed } from '@/types';

/**
 * Read-only demo reviews. targetId 'laundry' = the laundry's own reviews,
 * otherwise the review belongs to the matching sub-driver (Driver.id).
 *
 * Times are stored as "minutes ago" so they are ALWAYS in the past — the service
 * turns them into real dates at load time. Replaced by the reviews API later.
 *
 * Covers: a high review with a comment, a low review with a comment, a review
 * without a comment, a long comment, a mixed Arabic/English comment, a driver
 * with reviews, a driver without reviews, and a suspended driver who keeps
 * their review history.
 */
export const mockReviewSeeds: ReviewSeed[] = [
  /* ----------------------------- Laundry ----------------------------- */
  {
    id: 'RV-5001',
    targetId: 'laundry',
    customerName: 'أحمد العتيبي',
    rating: 5,
    comment: 'خدمة ممتازة وتنظيف دقيق، والتزمت المغسلة بموعد التسليم بالضبط.',
    createdMinutesAgo: 60,
    orderId: 'LB-1033',
  },
  {
    id: 'RV-5002',
    targetId: 'laundry',
    customerName: 'بدر العنزي',
    rating: 2,
    comment: 'تأخر الطلب يومًا كاملًا عن الموعد المحدد، وأتمنى تحسين التواصل مع العميل.',
    createdMinutesAgo: 1500,
    orderId: 'LB-1029',
  },
  {
    id: 'RV-5003',
    targetId: 'laundry',
    customerName: 'تركي السبيعي',
    rating: 4,
    comment: null,
    createdMinutesAgo: 2900,
    orderId: 'LB-1031',
  },
  {
    id: 'RV-5004',
    targetId: 'laundry',
    customerName: 'لمى الجهني',
    rating: 5,
    comment:
      'جودة الغسيل والكي ممتازة، والملابس عادت مرتبة ومعطرة بعناية. التعامل راقٍ من الاستلام حتى التسليم، وأقدّر التزام الفريق بالتفاصيل الدقيقة مثل فصل القطع الحساسة وتغليفها، وسأكرر التعامل مع المغسلة بكل تأكيد.',
    createdMinutesAgo: 4300,
    orderId: 'LB-1030',
  },
  {
    id: 'RV-5005',
    targetId: 'laundry',
    customerName: 'هند العسيري',
    rating: 3,
    comment: 'النتيجة النهائية كانت جيدة overall، لكن انتظرت أطول من المتوقع قليلًا.',
    createdMinutesAgo: 8600,
    orderId: 'LB-1024',
  },
  {
    id: 'RV-5006',
    targetId: 'laundry',
    customerName: 'غادة العنزي',
    rating: 4,
    comment: 'الأسعار مناسبة والخدمة جيدة، وأنصح بالتجربة.',
    createdMinutesAgo: 14400,
    orderId: 'LB-1023',
  },

  /* ------------------------- Driver DRV-3001 ------------------------- */
  {
    id: 'RV-6001',
    targetId: 'DRV-3001',
    customerName: 'ريم الدوسري',
    rating: 5,
    comment: 'المندوب كان محترمًا والتسليم كان سريعًا وفي الوقت المحدد.',
    createdMinutesAgo: 45,
    orderId: 'LB-1022',
  },
  {
    id: 'RV-6002',
    targetId: 'DRV-3001',
    customerName: 'تركي السبيعي',
    rating: 4,
    comment: 'تعامل جيد ووصل قبل الموعد.',
    createdMinutesAgo: 1700,
    orderId: 'LB-1031',
  },
  {
    id: 'RV-6003',
    targetId: 'DRV-3001',
    customerName: 'نورة الحربي',
    rating: 5,
    comment: null,
    createdMinutesAgo: 3000,
    orderId: 'LB-1021',
  },

  /* ------------------------- Driver DRV-3002 ------------------------- */
  {
    id: 'RV-6004',
    targetId: 'DRV-3002',
    customerName: 'خالد الشمري',
    rating: 4,
    comment: 'كل شيء تمام، بس لو كان التواصل قبل الوصول أفضل.',
    createdMinutesAgo: 1100,
    orderId: 'LB-1020',
  },
  {
    id: 'RV-6005',
    targetId: 'DRV-3002',
    customerName: 'لمى الجهني',
    rating: 5,
    comment: 'Delivered on time and the driver was very polite.',
    createdMinutesAgo: 1800,
    orderId: 'LB-1030',
  },
  {
    id: 'RV-6006',
    targetId: 'DRV-3002',
    customerName: 'وليد القرني',
    rating: 3,
    comment: 'الطلب وصل سليمًا لكن تأخر قليلًا عن الوقت المقدّر.',
    createdMinutesAgo: 6300,
    orderId: 'LB-1019',
  },

  /* ------------------------- Driver DRV-3003 ------------------------- */
  {
    id: 'RV-6007',
    targetId: 'DRV-3003',
    customerName: 'عبدالله الغامدي',
    rating: 5,
    comment: 'خدمة راقية والتزام تام بالوقت، شكرًا.',
    createdMinutesAgo: 90,
    orderId: 'LB-1033',
  },
  {
    id: 'RV-6008',
    targetId: 'DRV-3003',
    customerName: 'بدر العنزي',
    rating: 5,
    comment: 'Best delivery experience so far!',
    createdMinutesAgo: 1500,
    orderId: 'LB-1029',
  },

  /* -------- Driver DRV-3005 (suspended, keeps review history) -------- */
  {
    id: 'RV-6009',
    targetId: 'DRV-3005',
    customerName: 'عمر الثقفي',
    rating: 4,
    comment: null,
    createdMinutesAgo: 19000,
    orderId: 'LB-1018',
  },

  /* DRV-3004 has no reviews on purpose (the "not rated yet" case). */
];